import { GameDocument } from '../../../types/game-document';
import { DesignerSettings } from '../../../types/game-document/settings';
import { uuid } from '../../../types/common-helper';
import { MergeGameSettings } from './index';
import { ResourceEntity } from '../../../types/game-document/entities';
import {
  AddResourceAsync,
  AddResourceEntityAsync,
  GetResourceByName,
  UpdateResourceAsync
} from '../resources';
import {
  AllCorrectCustomFeedback,
  AllWrongCustomFeedback,
  MergeAssets,
  PartialCorrectCustomFeedback
} from '../assets';
import { Task } from '../../../types/game-document/entities/task';

/**
 * Adds a new Setting to the Game document.
 * @param gameDocument - The Game Document to modify
 * @returns The updated Game Document
 */
export const AddDesignerSettingAsync = async (gameDocument: GameDocument) => {
  let updatedGameDocument = gameDocument;
  const designerSettingResource: ResourceEntity[] = [
    {
      id: uuid(),
      name: 'Designer Setting - default available task icon',
      description: "The game's default available task icon",
      value: 'https://cdn.catalystglobal.games/resources/map-task.png',
      type: 'image'
    },
    {
      id: uuid(),
      name: 'Designer Setting - default completed task icon',
      description: "The game's default completed task icon",
      value:
        'https://cdn.catalystglobal.games/resources/map-task--complete.png',
      type: 'image'
    },
    {
      id: uuid(),
      name: 'Designer Setting - default feedback all correct icon',
      description: "The game's default completed feedback all correct icon",
      value:
        'https://stsharedappstorage.blob.core.windows.net/prod-catalyst-files/checked.png',
      type: 'image'
    },
    {
      id: uuid(),
      name: 'Designer Setting - default feedback all wrong icon',
      description: "The game's default completed feedback all wrong icon",
      value:
        'https://stsharedappstorage.blob.core.windows.net/prod-catalyst-files/cross.png',
      type: 'image'
    },
    {
      id: uuid(),
      name: 'Designer Setting - default feedback partially correct icon',
      description:
        "The game's default completed feedback partially correct icon",
      value:
        'https://stsharedappstorage.blob.core.windows.net/prod-catalyst-files/checked.png',
      type: 'image'
    }
  ];

  for (const res of designerSettingResource) {
    updatedGameDocument = await AddResourceEntityAsync(gameDocument, res);
  }

  const defaultAllCorrectFeedbackMessageResId =
    await DefaultAllCorrectFeedbackMessageResource(
      updatedGameDocument,
      'Congratulations, you got it!'
    );
  const defaultPartialCorrectFeedbackMessageResId =
    await DefaultPartialCorrectFeedbackMessageResource(
      updatedGameDocument,
      'Well done, you are partially correct.'
    );
  const defaultAllWrongFeedbackMessageResId =
    await DefaultAllWrongFeedbackMessageResource(
      updatedGameDocument,
      `That's not the answer!`
    );

  const designerSetting = {
    taskCompletionVisibility: false,
    defaultTaskTitleVisibility: false,
    defaultAreaTitleVisibility: false,
    defaultZoneTitleVisibility: false,
    defaultTaskAvailableIconResId: designerSettingResource[0].id,
    defaultTaskCompletedIconResId: designerSettingResource[1].id,
    defaultFeedbackAllCorrectIconResId: designerSettingResource[2].id,
    defaultFeedbackAllWrongIconResId: designerSettingResource[3].id,
    defaultFeedbackPartialCorrectIconResId: designerSettingResource[4].id,
    defaultAllCorrectFeedbackMessageResId,
    defaultPartialCorrectFeedbackMessageResId,
    defaultAllWrongFeedbackMessageResId,
    shouldGivePlayerScore: false,
    shouldGiveTeamScore: false
  };

  return MergeGameSettings(updatedGameDocument, designerSetting, 'designer');
};

export const AddDesignerSettingOnLoadAsync = async (
  gameDocument: GameDocument
): Promise<GameDocument> => {
  let updatedGameDocument = await EnsureDesignerSetting(gameDocument);

  const tasks = updatedGameDocument.assets.tasks;

  if (tasks?.length) {
    tasks.forEach((item) => {
      AddActionToTask(updatedGameDocument, item);
    });

    return MergeAssets(updatedGameDocument, tasks, 'tasks');
  }

  return updatedGameDocument;
};

export const AddGiveScoreToAllTask = async (
  gameDocument: GameDocument,
  identifier: string | 'player' | 'team'
) => {
  let updatedGameDocument = gameDocument;
  const tasks = updatedGameDocument.assets.tasks;

  if (tasks?.length) {
    tasks.forEach((item) => {
      AddGiveScoreToTask(item, identifier);
    });

    return MergeAssets(updatedGameDocument, tasks, 'tasks');
  }

  return updatedGameDocument;
};

export const EnsureDesignerSetting = async (
  gameDocument: GameDocument
): Promise<GameDocument> => {
  if (!gameDocument.settings?.designer) {
    return AddDesignerSettingAsync(gameDocument);
  }
  return gameDocument;
};

export const AddActionToTask = (
  gameDocument: GameDocument,
  task: Task
): void => {
  const { designer } = gameDocument.settings!;

  if (designer?.shouldGivePlayerScore) {
    AddGiveScoreToTask(task, 'player');
  }

  if (designer?.shouldGiveTeamScore) {
    AddGiveScoreToTask(task, 'team');
  }
};

export const AddGiveScoreToTask = (
  item: Task,
  identifier: string | 'player' | 'team'
) => {
  // Ensure events object exists
  if (!item.events) {
    item.events = {};
  }

  // Ensure onCompleteTask exists
  if (!item.events.onCompleteTask) {
    item.events.onCompleteTask = { steps: [] };
  }

  // Ensure steps exist
  let steps = item.events.onCompleteTask.steps ?? [];

  // Check if 'giveScore(score)' already exists, if not, add it
  if (
    !steps.some(
      (step) =>
        step.operation === 'giveScore(score)' && step.identifier === identifier
    )
  ) {
    steps.push({
      id: uuid(),
      action: 'Task.onComplete(score)',
      identifier: identifier,
      operation: 'giveScore(score)',
      argumentAssetId: '',
      argumentQuantity: 0,
      useFallbackValue: true
    });
  }

  // Assign the updated steps array back to onCompleteTask
  item.events.onCompleteTask.steps = steps;
};

export const DefaultAllCorrectFeedbackMessageResource = async (
  gameDocument: GameDocument,
  message: string
) => {
  let allCorrectMessageResource = await GetResourceByName(
    gameDocument!,
    'All correct feedback message default'
  );

  const defaultAllCorrectFeedbackMessageResId = allCorrectMessageResource
    ? allCorrectMessageResource.id
    : uuid();

  if (!allCorrectMessageResource) {
    await AllCorrectCustomFeedback(
      gameDocument,
      message,
      defaultAllCorrectFeedbackMessageResId
    );
  }

  if (allCorrectMessageResource && !allCorrectMessageResource.value) {
    const updatedResource = { ...allCorrectMessageResource, value: message };
    await UpdateResourceAsync(
      gameDocument,
      allCorrectMessageResource.id,
      updatedResource
    );
  }

  return defaultAllCorrectFeedbackMessageResId;
};

export const DefaultPartialCorrectFeedbackMessageResource = async (
  gameDocument: GameDocument,
  message: string
) => {
  let partialCorrectMessageResource = await GetResourceByName(
    gameDocument!,
    'Partial correct feedback message default'
  );

  const defaultPartialCorrectFeedbackMessageResId =
    partialCorrectMessageResource ? partialCorrectMessageResource.id : uuid();

  if (!partialCorrectMessageResource) {
    await PartialCorrectCustomFeedback(
      gameDocument,
      message,
      defaultPartialCorrectFeedbackMessageResId
    );
  }

  if (partialCorrectMessageResource && !partialCorrectMessageResource.value) {
    const updatedResource = {
      ...partialCorrectMessageResource,
      value: message
    };
    await UpdateResourceAsync(
      gameDocument,
      partialCorrectMessageResource.id,
      updatedResource
    );
  }

  return defaultPartialCorrectFeedbackMessageResId;
};

export const DefaultAllWrongFeedbackMessageResource = async (
  gameDocument: GameDocument,
  message: string
) => {
  let allWrongMessageResource = await GetResourceByName(
    gameDocument!,
    'All wrong feedback message default'
  );

  const defaultAllWrongFeedbackMessageResId = allWrongMessageResource
    ? allWrongMessageResource.id
    : uuid();

  if (!allWrongMessageResource) {
    await AllWrongCustomFeedback(
      gameDocument,
      message,
      defaultAllWrongFeedbackMessageResId
    );
  }

  if (allWrongMessageResource && !allWrongMessageResource.value) {
    const updatedResource = { ...allWrongMessageResource, value: message };
    await UpdateResourceAsync(
      gameDocument,
      allWrongMessageResource.id,
      updatedResource
    );
  }

  return defaultAllWrongFeedbackMessageResId;
};

/**
 * Updates the identified Setting in the Game Document.
 * @param gameDocument - The Game Document to modify
 * @param designerSetting - The new designer settings
 * @constructor
 */
export const UpdateDesignerSettingAsync = async (
  gameDocument: GameDocument,
  designerSetting: DesignerSettings
) => {
  return MergeGameSettings(gameDocument, designerSetting, 'designer');
};
